package com.kinoshazam.screens.allVideo.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.CompactVideoItem
import com.kinoshazam.uiKit.components.videoItem.compactBannerSkeletons
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.back
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.repeat
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosMVIAction
import presentation.screens.allVideos.AllVideosState

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CompactAllVideosContent(
    state: AllVideosState,
    onAction: (AllVideosMVIAction) -> Unit,
) {

    Scaffold(
        topBar = {
            TopAppBar(
                modifier = Modifier.statusBarsPadding(),
                title = {
                    TextLabel(
                        state = TextLabelState(
                            text = state.title,
                            color = UiKitTheme.colors.color_2,
                            textStyle = BoldLarge
                        )
                    )
                },
                navigationIcon = {
                    Image(
                        painter = painterResource(Res.drawable.back),
                        colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                        contentDescription = null,
                        modifier = Modifier
                            .padding(horizontal = 16.dp)
                            .clickable {
                                onAction(AllVideosMVIAction.BackPressed)
                            }
                    )
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = UiKitTheme.colors.color_3,
                ),
            )
        },
        containerColor = UiKitTheme.colors.color_3,
        content = { paddingValues ->
            when {
                state.isError -> ErrorScreen(onAction)
                else -> ContentScreen(paddingValues, state, onAction)
            }
        }
    )
}

@Composable
private fun ErrorScreen(onAction: (AllVideosMVIAction) -> Unit) {
    ErrorView(
        textError = stringResource(Res.string.error),
        textAction = stringResource(Res.string.repeat),
        onAction = { onAction(AllVideosMVIAction.OnReload) }
    )
}

@Composable
private fun LoadingScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
//        Text(
//            text = stringResource(Res.string.loading),
//            modifier = Modifier.align(Alignment.Center)
//        )
    }
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    state: AllVideosState,
    onAction: (AllVideosMVIAction) -> Unit,
) {
    val scrollState = rememberLazyGridState()
    val fetchNextPage: Boolean by remember {
        derivedStateOf {
            val currentVideoCount = scrollState.layoutInfo.totalItemsCount - 1
            val lastVisibleIndex =
                scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                    ?: return@derivedStateOf false
            return@derivedStateOf lastVisibleIndex >= currentVideoCount - 5
        }
    }

    LaunchedEffect(key1 = fetchNextPage) {
        if (fetchNextPage) {
            onAction(AllVideosMVIAction.FetchNextPage)
        }
    }

    LazyVerticalGrid(
        state = scrollState,
        modifier = Modifier.padding(paddingValues).padding(horizontal = 48.dp),
        contentPadding = PaddingValues(
            bottom = WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()
        ),
        verticalArrangement = Arrangement.spacedBy(4.dp),
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        columns = GridCells.Adaptive(minSize = 128.dp)
    ) {
        val videos = state.videos
        if (videos == null) {
            compactBannerSkeletons()
        } else itemsIndexed(videos) { index, video ->
            CompactVideoItem(
                state = VideoState(
                    id = video.id ?: -1,
                    title = video.title.orEmpty(),
                    image = video.imageUrl,
                    placeHolder = painterResource(Res.drawable.movie),
                    placeHolderColor = UiKitTheme.colors.color_5,
                    onAction = { id ->
                        onAction(AllVideosMVIAction.OpenDetails(id, video.isTvShow))
                    }
                ),
            )
        }
        if (state.isNextLoading) {
            item {
//                CircularProgressIndicator(
//                    modifier = Modifier.size(12.dp),
//                    color = UiKitTheme.colors.color_1,
//                    strokeWidth = 2.dp,
//                )
                Text(
                    text = "Load next page",
                    color = UiKitTheme.colors.color_2,
                )
            }
        }
    }
}